import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { BookOpen, Copy, Info, Loader2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Alert } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Pagination } from '@components/Pagination';
import { MediaThumb } from '@components/media/MediaThumb';
import { DocContent } from '@components/help/DocContent';
import { usePermissions } from '@hooks/usePermissions';
import mediaService from '@services/mediaService';
import landingService from '@services/landingService';
import type { Pagination as PaginationInfo } from '@/types/activity';
import type {
  DuplicateAction,
  DuplicateCandidate,
  DuplicateListStatus,
  DuplicateMatchKind,
  MediaAsset,
} from '@/types/media';
import { formatBytes, formatDateBR, formatDateTimeBR } from '@/utils/formatters';

const PAGE_SIZE = 10;

const MATCH_FILTERS: { value: DuplicateMatchKind | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'exact', label: 'Idênticas' },
  { value: 'near', label: 'Muito parecidas' },
  { value: 'similar', label: 'Semelhantes' },
];

/** Selo do nível de semelhança do par. */
const MatchBadge: React.FC<{ pair: DuplicateCandidate }> = ({ pair }) => {
  if (pair.match_kind === 'exact') return <span className="badge-danger">Idêntica</span>;
  if (pair.match_kind === 'near') return <span className="badge-warning">Muito parecida ({pair.distance} bits)</span>;
  const detail =
    pair.similarity !== undefined && pair.similarity !== null
      ? `${Math.round(pair.similarity * 100)}%`
      : `${pair.distance} bits`;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-violet-700 bg-violet-100 rounded-full">
      Semelhante ({detail})
    </span>
  );
};

const pixels = (m: MediaAsset) => (m.width ?? 0) * (m.height ?? 0);

const Side: React.FC<{ media: MediaAsset; label: string; better?: string; landingPages?: number }> = ({ media, label, better, landingPages }) => (
  <div className="flex-1 min-w-0 space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="flex flex-wrap gap-1">
        {!!landingPages && (
          <span className="badge-warning" title="Ao excluir esta, as páginas passam a usar a que ficar">
            Em {landingPages} landing page{landingPages > 1 ? 's' : ''}
          </span>
        )}
        {better && <span className="badge-success">{better}</span>}
      </span>
    </div>
    <Link to={`/media?media=${media.id}`} title="Abrir na biblioteca">
      <MediaThumb media={media} fit="contain" className="aspect-[4/3] rounded-lg bg-slate-900" />
    </Link>
    <div className="text-sm space-y-0.5">
      <p className="font-medium text-slate-800 truncate" title={media.original_file_name}>
        {media.title || media.original_file_name}
      </p>
      {media.title && <p className="text-xs text-slate-500 truncate">{media.original_file_name}</p>}
      <p className="text-xs text-slate-600">
        {[
          formatBytes(media.size_bytes),
          media.width && media.height ? `${media.width}×${media.height}` : '',
          (media.extension || '').replace(/^\./, '').toUpperCase(),
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p className="text-xs text-slate-500">
        {media.content_date && <>Conteúdo {formatDateBR(media.content_date)} · </>}
        Enviada {formatDateTimeBR(media.created_at)}
      </p>
      {media.event_name && <p className="text-xs text-slate-500">Evento: {media.event_name}</p>}
    </div>
    {media.tags.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {media.tags.map((t) => (
          <span key={t.tag_id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
            {t.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

/**
 * Pares de duplicadas detectados pelo worker (idênticas por sha256, muito parecidas por pHash e
 * semelhantes por pHash/sessão + descrição da IA). Manter um lado exclui (lógico) o outro e mescla
 * tags/produtos/evento no que ficou.
 */
export const MediaDuplicatesView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canResolve = checkPermission('media.delete').hasPermission;

  const [status, setStatus] = useState<DuplicateListStatus>('pending');
  const [matchKind, setMatchKind] = useState<DuplicateMatchKind | ''>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<DuplicateCandidate[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mediaService.duplicates({ status, matchKind, page, pageSize: PAGE_SIZE });
      setItems(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [status, matchKind, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Quantas landing pages usam cada mídia da tela (aviso ao lado da foto).
  const canLanding = checkPermission('landing.read').hasPermission;
  const [landingUse, setLandingUse] = useState<Record<number, number>>({});
  useEffect(() => {
    if (!canLanding) return;
    const ids = Array.from(new Set(items.flatMap((p) => [p.media_a.id, p.media_b.id]))).filter((id) => landingUse[id] === undefined);
    if (ids.length === 0) return;
    let alive = true;
    Promise.all(ids.map((id) => landingService.pagesUsingMedia(id).then((l) => [id, l.length] as const).catch(() => [id, 0] as const))).then(
      (pairs) => alive && setLandingUse((prev) => ({ ...prev, ...Object.fromEntries(pairs) })),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, canLanding]);

  const resolve = async (pair: DuplicateCandidate, action: DuplicateAction) => {
    setBusyId(pair.id);
    try {
      const res = await mediaService.resolveDuplicate(pair.id, action);
      const lp = res.landing_pages_updated
        ? ` ${res.landing_pages_updated} landing page${res.landing_pages_updated > 1 ? 's passaram' : ' passou'} a usar a mídia que ficou.`
        : '';
      setNotice(
        action === 'keep_both'
          ? 'Par marcado como "manter as duas".'
          : `Mantida a ${action === 'keep_a' ? 'esquerda' : 'direita'}; a outra foi excluída e as tags/produtos/evento foram mesclados.${lp}`,
      );
      if (res.warning) setError(new Error(res.warning));
      // Tira o par da lista na hora; se a página esvaziar, recarrega.
      setItems((prev) => prev.filter((p) => p.id !== pair.id));
      if (items.length <= 1) {
        if (page > 1) setPage((p) => p - 1);
        else load();
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  const tabClass = (active: boolean) =>
    clsx(
      'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
      active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700',
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Copy size={20} className="text-slate-500" /> Duplicadas
        </h1>
        <div className={clsx('flex items-center gap-2', showHelp && 'invisible')}>
          <label className="text-label" htmlFor="dup-kind">
            Nível
          </label>
          <select
            id="dup-kind"
            className="select-field w-auto"
            value={matchKind}
            onChange={(e) => {
              setMatchKind(e.target.value as DuplicateMatchKind | '');
              setPage(1);
            }}
          >
            {MATCH_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border-b border-slate-200 flex">
        <button type="button" className={tabClass(!showHelp && status === 'pending')} onClick={() => { setShowHelp(false); setStatus('pending'); setPage(1); }}>
          Pendentes
        </button>
        <button type="button" className={tabClass(!showHelp && status === 'resolved')} onClick={() => { setShowHelp(false); setStatus('resolved'); setPage(1); }}>
          Resolvidas
        </button>
        <button
          type="button"
          className={clsx(tabClass(showHelp), 'ml-auto inline-flex items-center gap-2')}
          onClick={() => setShowHelp(true)}
        >
          <BookOpen size={16} /> Como funciona
        </button>
      </div>

      {showHelp ? (
        <DocContent rota="/media/duplicates" />
      ) : (
        <>

      {notice && (
        <Alert variant="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading && items.length === 0 && (
        <p className="text-center text-slate-400 py-12">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      )}
      {!loading && items.length === 0 && (
        <div className="card text-center py-12">
          <Copy size={32} className="text-slate-300 mx-auto mb-2" />
          <p className="text-slate-600 font-medium">
            {status === 'pending' ? 'Nenhuma duplicada pendente.' : 'Nenhuma duplicada resolvida.'}
          </p>
        </div>
      )}

      <div className={clsx('space-y-4', loading && 'opacity-60')}>
        {items.map((pair) => {
          const pa = pixels(pair.media_a);
          const pb = pixels(pair.media_b);
          const betterA = pa > pb ? 'Maior resolução' : undefined;
          const betterB = pb > pa ? 'Maior resolução' : undefined;
          const similar = pair.match_kind === 'similar';
          const busy = busyId === pair.id;
          return (
            <div key={pair.id} className="card p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <MatchBadge pair={pair} />
                  <span className="text-small">detectada em {formatDateTimeBR(pair.created_at)}</span>
                </div>
                {pair.status !== 'pending' && (
                  <span className="text-small">
                    {pair.status === 'kept_both' ? 'Mantidas as duas' : 'Resolvida'}
                    {pair.resolved_at && ` em ${formatDateTimeBR(pair.resolved_at)}`}
                    {pair.resolved_by && ` · ${pair.resolved_by}`}
                  </span>
                )}
              </div>

              {similar && pair.status === 'pending' && (
                <p className="flex items-start gap-2 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-sm text-violet-800">
                  <Info size={16} className="flex-shrink-0 mt-0.5" />
                  Fotos semelhantes costumam ser cliques diferentes da mesma cena (outro ângulo, outra pose). Na dúvida,
                  mantenha as duas — exclua uma só quando for realmente repetida.
                </p>
              )}

              <div className="flex flex-col md:flex-row gap-4">
                <Side media={pair.media_a} label="Esquerda" better={betterA} landingPages={landingUse[pair.media_a.id]} />
                <Side media={pair.media_b} label="Direita" better={betterB} landingPages={landingUse[pair.media_b.id]} />
              </div>

              {pair.status === 'pending' && canResolve && (
                <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => resolve(pair, 'keep_a')}>
                    Manter esquerda
                  </Button>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => resolve(pair, 'keep_b')}>
                    Manter direita
                  </Button>
                  <Button variant={similar ? 'primary' : 'outline'} size="sm" loading={busy} onClick={() => resolve(pair, 'keep_both')}>
                    Manter as duas{similar ? ' (sugerido)' : ''}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pagination && items.length > 0 && (
        <div className="card overflow-hidden">
          <Pagination
            page={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_items}
            onChange={setPage}
          />
        </div>
      )}

        </>
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
